import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  TrendingDown,
  Star
} from '@mui/icons-material';

const ComparisonPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { products, userProfile } = location.state || {};

  if (!products || products.length < 2) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Please select at least 2 products to compare.
        </Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  const handleBuyNow = (product) => {
    navigate('/checkout', {
      state: {
        product,
        userProfile
      }
    });
  };

  const getFeature = (product, featureName) => {
    const raw = product.key_features;
    if (!raw) return false;
    let features = {};
    if (typeof raw === 'string') {
      try {
        features = JSON.parse(raw) || {};
      } catch {
        features = {};
      }
    } else if (typeof raw === 'object' && !Array.isArray(raw)) {
      features = raw;
    }
    return features[featureName];
  };

  const renderCheck = (value) => {
    return value ? (
      <CheckCircle sx={{ color: 'success.main' }} />
    ) : (
      <Cancel sx={{ color: 'error.main', opacity: 0.3 }} />
    );
  };

  const isCurrentColumn = userProfile?.current_premium && products.length >= 1;
  const colSpan = 1 + (isCurrentColumn ? 1 : 0) + products.length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        Back to Results
      </Button>

      <Typography variant="h4" gutterBottom>
        Compare Plans & Save
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Side-by-side comparison of your selected insurance plans
      </Typography>

      {products[0]?.match_score >= 85 && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Star sx={{ color: 'success.dark' }} />
            <Typography variant="body1" color="success.dark">
              Based on your profile, <strong>{products[0].product_name}</strong> offers the best value
            </Typography>
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} />
              {isCurrentColumn && (
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.200' }}>
                  <Typography variant="subtitle2">YOUR CURRENT</Typography>
                  <Typography variant="caption" color="text.secondary">PLAN</Typography>
                </TableCell>
              )}
              {products.map((product, index) => (
                <TableCell key={product.id} align="center" sx={{ bgcolor: index === 0 ? 'primary.light' : 'grey.50' }}>
                  <Box sx={{ p: 1 }}>
                    {product.insurer_logo && (
                      <Box
                        component="img"
                        src={product.insurer_logo}
                        alt={product.insurer_name || ''}
                        sx={{ height: 40, objectFit: 'contain', mb: 1 }}
                      />
                    )}
                    <Typography variant="subtitle2" fontWeight="bold">
                      {product.product_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {product.insurer_name}
                    </Typography>
                    {product.match_score != null && product.match_score >= 85 && (
                      <Chip
                        label={`${product.match_score}% Match`}
                        size="small"
                        color="success"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
                PRICING
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Price/year</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography variant="h6">${userProfile.current_premium}</Typography>
                </TableCell>
              )}
              {products.map((product) => {
                const premium = Number(product.personalized_premium ?? product.base_premium) || 0;
                return (
                  <TableCell key={product.id} align="center">
                    <Typography variant="h6" color="primary">
                      ${premium.toLocaleString()}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Monthly</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography>${Math.round(userProfile.current_premium / 12)}</Typography>
                </TableCell>
              )}
              {products.map((product) => {
                const premium = Number(product.personalized_premium ?? product.base_premium) || 0;
                return (
                  <TableCell key={product.id} align="center">
                    <Typography>${Math.round(premium / 12)}</Typography>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Savings</TableCell>
              {isCurrentColumn && <TableCell align="center">—</TableCell>}
              {products.map((product) => {
                const savings = product.savings;
                if (savings && savings.amount !== 0) {
                  return (
                    <TableCell key={product.id} align="center">
                      <Chip
                        label={savings.amount > 0 ? `SAVE $${savings.amount}` : `+$${Math.abs(savings.amount)}`}
                        color={savings.amount > 0 ? 'success' : 'error'}
                        icon={savings.amount > 0 ? <TrendingDown /> : undefined}
                        size="small"
                      />
                    </TableCell>
                  );
                }
                return <TableCell key={product.id} align="center">—</TableCell>;
              })}
            </TableRow>

            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
                COVERAGE
              </TableCell>
            </TableRow>

            {['inpatient', 'outpatient', 'dental', 'optical', 'maternity'].map((feature) => (
              <TableRow key={feature}>
                <TableCell sx={{ textTransform: 'capitalize' }}>{feature}</TableCell>
                {isCurrentColumn && (
                  <TableCell align="center">
                    {renderCheck(feature === 'inpatient' || feature === 'outpatient')}
                  </TableCell>
                )}
                {products.map((product) => (
                  <TableCell key={product.id} align="center">
                    {renderCheck(getFeature(product, feature))}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
                NETWORK
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Hospitals</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography>38</Typography>
                </TableCell>
              )}
              {products.map((product) => (
                <TableCell key={product.id} align="center">
                  <Typography>{product.network_hospital_count ?? 45}</Typography>
                </TableCell>
              ))}
            </TableRow>

            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
                PERFORMANCE
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Claim approval rate</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography>89%</Typography>
                </TableCell>
              )}
              {products.map((product) => (
                <TableCell key={product.id} align="center">
                  <Typography>{product.claims_approval_rate ?? 95}%</Typography>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell>Avg processing time</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography>5 days</Typography>
                </TableCell>
              )}
              {products.map((product) => (
                <TableCell key={product.id} align="center">
                  <Typography>{product.avg_claim_processing_days ?? 3} days</Typography>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell>Customer score</TableCell>
              {isCurrentColumn && (
                <TableCell align="center">
                  <Typography>4.2/5</Typography>
                </TableCell>
              )}
              {products.map((product) => (
                <TableCell key={product.id} align="center">
                  <Typography>
                    {product.avg_review_rating != null
                      ? Number(product.avg_review_rating).toFixed(1)
                      : '4.7'}
                    /5
                  </Typography>
                </TableCell>
              ))}
            </TableRow>

            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
                TUTAMEN PERKS
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Cashback (Bronze tier)</TableCell>
              {isCurrentColumn && <TableCell align="center">—</TableCell>}
              {products.map((product) => {
                const premium = Number(product.personalized_premium ?? product.base_premium) || 0;
                const cashback = Math.round(premium * 0.05);
                return (
                  <TableCell key={product.id} align="center">
                    <Typography color="success.main" fontWeight="bold">
                      ${cashback}/year
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Total Benefit</TableCell>
              {isCurrentColumn && <TableCell align="center">—</TableCell>}
              {products.map((product) => {
                const savings = product.savings?.amount || 0;
                const premium = Number(product.personalized_premium ?? product.base_premium) || 0;
                const cashback = Math.round(premium * 0.05);
                const total = savings + cashback;

                return (
                  <TableCell
                    key={product.id}
                    align="center"
                    sx={{ bgcolor: total > 0 ? 'success.light' : 'inherit' }}
                  >
                    <Typography variant="h6" color={total > 0 ? 'success.dark' : 'inherit'}>
                      {total > 0 ? `SAVE $${total}` : total < 0 ? `+$${Math.abs(total)}` : '—'}
                    </Typography>
                    {total > 0 && <Typography variant="caption">per year</Typography>}
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} />
              {isCurrentColumn && (
                <TableCell align="center">
                  <Chip label="Current" size="small" />
                </TableCell>
              )}
              {products.map((product) => (
                <TableCell key={product.id} align="center" sx={{ py: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleBuyNow(product)}
                    size="large"
                  >
                    Buy Now
                  </Button>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ComparisonPage;
